import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { goToTopOfPage, scrollToArea } from '../../../helper/helper';
import { useMediaQuery } from 'react-responsive';
import Modal from '../../../components/Common/modal/Modal';
import { CONFIG } from '../../../config/mainConfig';
import Button from '../../../components/Common/Button';
import { setStoryData, setStoryNewCharacter } from '../../../store/storySlice';
import { setCurrentStep } from '../../../store/appSlice';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { callImageGenerator, updateText } from '../services/character.service';
import { EditImageTextIllustrationProps } from '../models/EditImageTextIllustrationProps.interface';
import { MidjourneyModeGeneration } from '../../../interfaces/book.interface';
import { ISettingsBookAgeModel } from '../../../interfaces/settings.interface';
import { SecureImg } from '../../../components/Common/SecureImage';
import { Toast } from '../../../components/Common/Toast';

const EditImageTextIllustration = ({
  bookId,
  pageIndex,
  pageData,
  type,
  index,
  setShowEditTextIllustration,
  setShowLowCreditModal,
  displayBookPages,
  setStateData,
  stateData,
  targetRefs,
  setShowStoryForm,
  setimageCharacterPrompt,
  editing,
  setShowCharacterLibrary,
}: EditImageTextIllustrationProps) => {
  const navigate = useNavigate();
  const textRef = useRef<HTMLTextAreaElement>(null);
  const sceneRef = useRef<HTMLTextAreaElement>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [ageConfig, setAgeConfig] = useState<ISettingsBookAgeModel | null>(null);

  const [showRelaxModeModal, setShowRelaxModeModal] = useState<boolean>(false);

  const bookSettingsAge = useSelector((state: RootState) => state.app.bookSettingsAge);
  const bookData = useSelector((state: RootState) => state.app.bookData);
  const storyData = useSelector((state: RootState) => state.story.storyData);
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    goToTopOfPage();
    setStateData({ ...stateData, showDownloadPDF: false });
  }, []);

  useEffect(() => {
    if (bookSettingsAge && bookData?.childAge) {
      // @ts-ignore
      setAgeConfig(bookSettingsAge[bookData?.childAge]);
    }
  }, [bookData]);

  useEffect(() => {
    if (textRef.current && pageData) {
      const textLength = textRef.current.value.length;
      textRef.current.setSelectionRange(textLength, textLength);
      textRef.current.focus();
    }
    if (sceneRef.current && pageData) {
      const sceneLength = sceneRef.current.value.length;
      sceneRef.current.setSelectionRange(sceneLength, sceneLength);
      sceneRef.current.focus();
    }
  }, [pageData]);

  const handleImageGeneration = async (pageKey: string, storyPrompt: string, storyText: string) => {
    await callImageGenerator({
      pageKey,
      storyPrompt,
      storyText,
      bookId,
      pageData,
      bookData,
      storyData,
      setLoader,
      dispatch,
      setShowEditTextIllustration,
      setShowLowCreditModal,
      closeComponent,
    });
  };

  const handleUpdateText = async (updatedText: string, updatedScene: string, ignoreImageGeneration?: boolean) => {
    await updateText({
      bookId,
      pageIndex,
      updatedText,
      updatedScene,
      setLoader,
      displayBookPages,
      setShowEditTextIllustration,
      dispatch,
      storyData,
      closeComponent,
      ignoreImageGeneration,
    });
  };

  const closeComponent = () => {
    scrollToArea(targetRefs, pageIndex.toString());
    setStateData({ ...stateData, showDownloadPDF: true });
  };

  const handleUpdate = async (generateNewImage = true) => {
    const isWithinCreditLimit =
      bookData?.modeIllustrationGeneration === MidjourneyModeGeneration.relaxed ||
      (+(bookData?.creditsPerBookLimit || 0) >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE &&
        +(storyData?.creditsPerBookLimit ?? 0) >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE);
    const updatedText = textRef.current ? textRef.current.value : '';
    const updatedScene = sceneRef.current ? sceneRef.current.value : '';


    if (!generateNewImage) {
      const validationMessage = getTextError(updatedText);
      setTextError(validationMessage);
      setShowTextError(true);
      if (validationMessage) {
        Toast(validationMessage, true);
        return;
      }
    }

    if (!generateNewImage && pageData.scene === updatedScene) {
      await handleUpdateText(updatedText, updatedScene, true);
    } else {
      if (!isWithinCreditLimit) {
        setShowRelaxModeModal(true);
      } else {
        const creditsToDeduct =
          bookData?.modeIllustrationGeneration === MidjourneyModeGeneration.relaxed
            ? 0
            : CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE;

        dispatch(
          setStoryData({
            ...storyData,
            creditsPerBookLimit: +(bookData?.creditsPerBookLimit || 0) - creditsToDeduct,
          }),
        );
        if (pageData.scene != updatedScene || pageData.text != updatedText)
          await handleUpdateText(updatedText, updatedScene);
        if (generateNewImage) {
          await handleImageGeneration(pageIndex.toString(), updatedScene, updatedText);
        }
      }
    }
  };
  const isLargeScreen = useMediaQuery({ query: '(min-width: 1024px)' });

  const [text, setText] = useState(pageData?.text || '');
  const [textError, setTextError] = useState<string | null>(null);
  const [showTextError, setShowTextError] = useState<boolean>(false);

  const getTextError = (value: string): string | null => {
    const sanitizedValue = value.trim();
    const wordLimit = +(bookData?.wordRange || 0);
    const maxTextLength = +(ageConfig?.max_text_length || 0);

    if (wordLimit > 0) {
      const words = sanitizedValue ? sanitizedValue.split(/\s+/).length : 0;
      if (words > wordLimit) {
        return `Word count for current book is ${wordLimit}!`;
      }
    }

    if (maxTextLength > 0 && sanitizedValue.length > maxTextLength) {
      return `Text limit reached! Maximum allowed is ${maxTextLength} characters.`;
    }

    return null;
  };

  const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    if (type === 'editText') {
      setText(nextValue);
      return;
    }
    const errorMessage = getTextError(nextValue);
    setText(nextValue);
    setTextError(errorMessage);
    setShowTextError(!!errorMessage);
  };

  const handleSelectNewCharacter = (): void => {
    dispatch(setStoryNewCharacter(true));
    setimageCharacterPrompt(editing);
    setShowCharacterLibrary(false);
    setShowStoryForm(true);
    dispatch(setCurrentStep(2));

    dispatch(
      setStoryData({
        ...storyData,
        illustrationCharacter: editing,
        uploadedImage: null,
        regeneratedNewCharacter: false,
        newRefImageUploaded: false,
      }),
    );
  };

  const handleClose = (): void => {
    closeComponent();
    setShowEditTextIllustration(false);
    dispatch(
      setStoryData({
        ...storyData,
        illustrationCharacter: null,
      }),
    );
  };

  return (
    <>
      {showRelaxModeModal && bookData?.modeIllustrationGeneration !== MidjourneyModeGeneration.relaxed && (
        <Modal
          // title="High Speed Image Renewal Alert"
          description="2Sorry, you have run out of image credits. You can still access all images you’ve created so far by clicking on ‘Image Library’."
          doProcess={() => setShowRelaxModeModal(false)}
          showCloseButton={false}
          showSubmitButton={true}
          submitButtonTitle={'Continue'}
        />
      )}
      <div
        className={`${
          isLargeScreen
            ? 'animate__animated animate__slideInRight flex justify-evenly gap-8 md:gap-12 flex-wrap md:flex-nowrap text-left m-2 mb-8 text-sm text-storyblue'
            : 'flex justify-evenly gap-8 md:gap-12 flex-wrap md:flex-nowrap text-left m-2 mb-8 text-sm text-storyblue'
        }`}
      >
        {/*  */}
        <div className="w-full">
          <div className="flex justify-between">
            <div className="w-full">
              <h2 className="text-left text-xl md:text-2xl mb-4 font-bold text-storyblue">
                Edit Page {index + 4}
                {type === 'page' ? `- ${pageData?.index! + 1}` : ''}
              </h2>
              <div className={`grid ${type === 'page' ? 'grid-cols-3 ' : 'grid grid-cols-2 gap-4'} md: gap-6`}>
                <div
                  className="col-span-2"
                  style={{
                    display: type === 'editText' || type === 'page' ? 'block' : 'none',
                  }}
                >
                  <h3 className="w-full text-sm text-gray-500 font-bold">Page Text </h3>
                  <textarea
                    ref={textRef}
                    value={text}
                    onInput={handleTextChange}
                    className={`h-36 md:h-96 text-lg md:text-2xl p-4 border-2 text-gray-800 w-full ${
                      textError ? 'border-red-500 focus:border-red-500' : 'border-gray-400'
                    }`}
                  />
                </div>
                <div className="my-2" style={{ display: type === 'page' ? 'block' : 'none' }}>
                  <SecureImg
                    storageSrc={pageData?.imageURLwebp}
                    alt="Page"
                    className="mx-auto mt-4 w-48 md:w-96 h-48 md:h-96"
                  />
                </div>
                <div
                  className="col-span-2"
                  style={{
                    display: type === 'editPrompt' || type === 'page' ? 'block' : 'none',
                  }}
                >
                  <h3 className="text-sm text-gray-500 font-bold pb-2">
                    This scene description allows you to modify this pages illustration featuring the character selected
                    on the right.
                  </h3>
                  {pageData?.contentFilter?.flagged ? (
                    <div className="mb-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
                      <p className="font-semibold mb-1">Let's tweak this scene</p>
                      <p>{pageData.contentFilter.guidance}</p>
                    </div>
                  ) : pageData?.error ? (
                    // Generic-failure banner — shown when the page is in
                    // an error state that isn't moderation. The message is
                    // intentionally generic; saving the scene re-fires
                    // generation, which is the only recovery path.
                    <div className="mb-3 rounded-lg border-l-4 border-storyerror bg-red-50 p-3 text-sm text-red-900">
                      <p className="font-semibold mb-1">We couldn't generate this image</p>
                      <p>Something went wrong. Save your scene to try again.</p>
                    </div>
                  ) : null}
                  <textarea
                    autoFocus
                    ref={sceneRef}
                    defaultValue={pageData?.scene}
                    className={`h-36 md:h-96 text-lg md:text-2xl p-4 border-2 text-gray-800 w-full ${
                      pageData?.contentFilter?.flagged ? 'border-amber-500 focus:border-amber-500' : 'border-gray-400'
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center flex-wrap gap-2 md:gap-4 mt-4">
            <Button
              title=" Close"
              onClick={() => handleClose()}
              className="submit-button flex justify-center gap-3 max-w-7xl bg-storyblueSecondLightText  text-white font-bold rounded-2xl px-4 py-2 text-base items-center"
            />
            <Button
              title={type === 'editText' ? 'Update Text' : 'New Image'}
              loader={loader}
              onClick={() => {
                handleUpdate(type !== 'editText');
              }}
              className="submit-button flex justify-center gap-3 max-w-7xl bg-storyblue text-white font-bold rounded-2xl px-4 py-2 text-base items-center"
            />
          </div>
        </div>
        <div
          className="flex flex-col gap-[4.75rem] lg:gap-10 w-full md:w-[50%]"
          style={{
            display: type === 'editPrompt' || type === 'page' ? 'flex' : 'none',
          }}
        >
          <h2 className="text-left text-xl md:text-2xl font-bold text-storyblue whitespace-nowrap pb-2">
            Character for Illustration
          </h2>
          <div className="flex flex-col items-center gap-4">
            <SecureImg
              storageSrc={
                pageData?.imagePromptCharacterUrl ? pageData?.imagePromptCharacterUrl : bookData?.characterPortrait
              }
              className="md:h-[24rem] rounded-md"
            />
            <Button
              title={'Select New Character'}
              onClick={() => handleSelectNewCharacter()}
              className="submit-button flex justify-center gap-3 max-w-7xl bg-storyblue text-white font-bold rounded-2xl px-4 py-2 text-base items-center"
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default EditImageTextIllustration;
